// src/components/WsListView.js - Wholesale List View
import React, { useState, useCallback, useContext } from 'react';
import { useCart } from '../hooks/useCart';
import { getImageUrl } from '../utils/getImageUrl';
import { useProductOrderStatsContext } from '../context/ProductOrderStatsContext';
import { AuthContext } from '../context/AuthContext';
import useAccess from '../hooks/useAccess';
import { mostPermissive } from '../permissions/access';

const WsListView = ({ products, isMobile, lastProductRef, hoverZoomEnabled, showStock = false }) => {
  const { isAuthenticated } = useContext(AuthContext);
  const { can: canAccess, stateOf: accessStateOf } = useAccess();
  const can = (key) => !isAuthenticated || canAccess(key);
  const stateOf = (key) => (isAuthenticated ? accessStateOf(key) : mostPermissive(key));
  const {
    setItemQuantity,
    removeItem: removeFromCart,
    getProductTotalQuantity,
    items: cartItems
  } = useCart();
  const [quantities, setQuantities] = useState({});
  const [actionStates, setActionStates] = useState({});
  const [longPressStates, setLongPressStates] = useState({});
  const [hoverImage, setHoverImage] = useState(null);
  
  const { getOrderStatsForProduct, ensureProductsFetched } = useProductOrderStatsContext();

  const productIds = React.useMemo(() => products.map(p => p.id).filter(id => id != null), [products]);

  const productIdsString = React.useMemo(() => productIds.join(','), [productIds]);

  React.useEffect(() => {
    if (productIds.length > 0) {
      ensureProductsFetched(productIds);
    }
  }, [productIdsString, ensureProductsFetched]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleQuantityChange = (productId, value) => {
    // Allow manual input, clean number input
    const numericValue = value.replace(/[^0-9.]/g, '');
    setQuantities(prev => ({
      ...prev,
      [productId]: numericValue
    }));
  };

  const handleQuantityFocus = (productId) => {
    // When focusing on a field, initialize with current cart quantity if not already set
    const cartQuantity = getProductTotalQuantity(productId);
    setQuantities(prev => ({
      ...prev,
      [productId]: prev[productId] !== undefined ? prev[productId] : (cartQuantity > 0 ? cartQuantity.toString() : '')
    }));
  };

  const handleLongPressStart = (productId) => {
    // Start long press timer
    const timer = setTimeout(async () => {
      // Clear all quantity after 2 seconds
      const cartItem = cartItems.find(item => item.product?.id === productId);
      if (cartItem) {
        setLongPressStates(prev => ({ ...prev, [productId]: { clearing: true } }));
        await removeFromCart(cartItem.id);
        setLongPressStates(prev => ({ ...prev, [productId]: { cleared: true } }));
        
        // Clear the cleared state after short delay
        setTimeout(() => {
          setLongPressStates(prev => ({ ...prev, [productId]: {} }));
        }, 1000);
      }
    }, 2000);

    setLongPressStates(prev => ({
      ...prev,
      [productId]: { timer, longPressing: true }
    }));
  };

  const handleLongPressEnd = (productId) => {
    // Cancel long press timer
    const longPressState = longPressStates[productId];
    if (longPressState?.timer) {
      clearTimeout(longPressState.timer);
    }
    setLongPressStates(prev => ({ ...prev, [productId]: {} }));
  };

  const roundToNearestStep = (value, minQuantity, stepQuantity) => {
    const min = parseFloat(minQuantity || 1);
    const step = parseFloat(stepQuantity || 1);
    const inputValue = parseFloat(value || 0);
    
    // If input is less than minimum, return minimum
    if (inputValue < min) {
      return min;
    }
    
    // Calculate how many steps above minimum
    const stepsAboveMin = Math.round((inputValue - min) / step);
    
    // Return minimum + (rounded steps * step size)
    return min + (stepsAboveMin * step);
  };

  const updateCartQuantity = async (product, quantity) => {
    const cartQuantity = getProductTotalQuantity(product.id);

    if (quantity > 0 && quantity !== cartQuantity) {
      try {
        setActionStates(prev => ({ ...prev, [product.id]: { loading: true } }));

        // Create unit object with base unit
        const unitObj = {
          id: product.unit?.id || null,
          symbol: product.base_unit || product.unit?.symbol || 'pcs',
          conversion_factor: 1
        };

        // Calculate unit price (base price * conversion factor)
        const unitPrice = parseFloat(product.price || 0) * parseFloat(unitObj.conversion_factor || 1);

        // Use setItemQuantity to SET the quantity directly (not add)
        await setItemQuantity(product, unitObj, quantity, unitPrice);

        // Clear local quantity after updating cart so it shows actual cart quantity
        setQuantities(prev => {
          const newQuantities = { ...prev };
          delete newQuantities[product.id];
          return newQuantities;
        });

        setActionStates(prev => ({ ...prev, [product.id]: { success: true } }));

        // Clear success state after short delay
        setTimeout(() => {
          setActionStates(prev => ({ ...prev, [product.id]: {} }));
        }, 1000);
      } catch (error) {
        console.error('Error updating cart:', error);
        setActionStates(prev => ({
          ...prev,
          [product.id]: { error: true, errorMessage: error.message }
        }));

        // Clear error state after delay
        setTimeout(() => {
          setActionStates(prev => ({ ...prev, [product.id]: {} }));
        }, 3000);
      }
    } else if (quantity === 0 && cartQuantity > 0) {
      // Remove item if quantity is set to 0
      const existingCartItems = cartItems.filter(item => item.product?.id === product.id);
      if (existingCartItems.length > 0) {
        await removeFromCart(existingCartItems[0].id);
      }
      setQuantities(prev => {
        const newQuantities = { ...prev };
        delete newQuantities[product.id];
        return newQuantities;
      });
    }
  };

  const handleQuantityKeyPress = async (e, product) => {
    if (e.key === 'Enter') {
      const inputQuantity = parseFloat(quantities[product.id] || 0);
      
      if (inputQuantity > 0) {
        // Round to nearest step-divisible quantity
        const roundedQuantity = roundToNearestStep(
          inputQuantity, 
          product.min_quantity, 
          product.step_quantity
        );
        
        // Update the input field with rounded value
        setQuantities(prev => ({
          ...prev,
          [product.id]: roundedQuantity.toString()
        }));
        
        // Update cart with rounded quantity
        await updateCartQuantity(product, roundedQuantity);
      } else {
        await updateCartQuantity(product, inputQuantity);
      }
    }
  };

  const handleQuantityBlur = async (e, product) => {
    const inputQuantity = parseFloat(quantities[product.id] || 0);
    
    if (inputQuantity > 0) {
      // Round to nearest step-divisible quantity
      const roundedQuantity = roundToNearestStep(
        inputQuantity, 
        product.min_quantity, 
        product.step_quantity
      );
      
      // Update the input field with rounded value
      setQuantities(prev => ({
        ...prev,
        [product.id]: roundedQuantity.toString()
      }));
      
      // Update cart with rounded quantity
      await updateCartQuantity(product, roundedQuantity);
    } else {
      await updateCartQuantity(product, inputQuantity);
    }
  };

  const handleDirectAddToCart = async (product, quantityToAdd = null) => {
    try {
      setActionStates(prev => ({ ...prev, [product.id]: { loading: true } }));

      // Get current cart quantity
      const currentCartQuantity = getProductTotalQuantity(product.id);

      // Get wholesale settings with fallback defaults
      const minQuantity = parseFloat(product.min_quantity || 1);
      const stepQuantity = parseFloat(product.step_quantity || 1);

      let finalQuantity;

      if (quantityToAdd !== null) {
        // Manual quantity specified - ADD to current
        finalQuantity = currentCartQuantity + quantityToAdd;
      } else {
        // Auto quantity based on wholesale rules
        if (currentCartQuantity === 0) {
          // First add - use minimum quantity
          finalQuantity = minQuantity;
        } else {
          // Subsequent adds - add step quantity
          finalQuantity = currentCartQuantity + stepQuantity;
        }
      }

      // Create unit object with base unit
      const unitObj = {
        id: product.unit?.id || null,
        symbol: product.base_unit || product.unit?.symbol || 'pcs',
        conversion_factor: 1
      };

      // Calculate unit price (base price * conversion factor)
      const unitPrice = parseFloat(product.price || 0) * parseFloat(unitObj.conversion_factor || 1);

      // Use setItemQuantity to SET the final quantity
      await setItemQuantity(product, unitObj, finalQuantity, unitPrice);

      setActionStates(prev => ({ ...prev, [product.id]: { success: true } }));

      // Clear success state after short delay
      setTimeout(() => {
        setActionStates(prev => ({ ...prev, [product.id]: {} }));
      }, 1000);
    } catch (error) {
      console.error('Error adding to cart:', error);
      setActionStates(prev => ({
        ...prev,
        [product.id]: { error: true, errorMessage: error.message }
      }));

      // Clear error state after delay
      setTimeout(() => {
        setActionStates(prev => ({ ...prev, [product.id]: {} }));
      }, 3000);
    }
  };

  const handleDecreaseCartQuantity = async (product) => {
    try {
      setActionStates(prev => ({ ...prev, [product.id]: { loading: true } }));

      // Get current cart quantity
      const currentQuantity = getProductTotalQuantity(product.id);

      if (currentQuantity > 0) {
        // Get wholesale settings with fallback defaults
        const stepQuantity = parseFloat(product.step_quantity || 1);

        // Decrease by step quantity
        const newQuantity = currentQuantity - stepQuantity;

        // Create unit object with base unit
        const unitObj = {
          id: product.unit?.id || null,
          symbol: product.base_unit || product.unit?.symbol || 'pcs',
          conversion_factor: 1
        };

        // Calculate unit price
        const unitPrice = parseFloat(product.price || 0) * parseFloat(unitObj.conversion_factor || 1);

        // Use setItemQuantity - it will remove item if quantity <= 0
        await setItemQuantity(product, unitObj, newQuantity, unitPrice);
      }

      setActionStates(prev => ({ ...prev, [product.id]: { success: true } }));

      // Clear success state after short delay
      setTimeout(() => {
        setActionStates(prev => ({ ...prev, [product.id]: {} }));
      }, 1000);
    } catch (error) {
      console.error('Error decreasing quantity:', error);
      setActionStates(prev => ({
        ...prev,
        [product.id]: { error: true, errorMessage: error.message }
      }));

      // Clear error state after delay
      setTimeout(() => {
        setActionStates(prev => ({ ...prev, [product.id]: {} }));
      }, 3000);
    }
  };

  // Fallback image URL - same as other views
  const FALLBACK_IMAGE = "/static/images/no-image.png";
  
  // Handle image error - same as other views
  const handleImageError = useCallback((e) => {
    e.target.onerror = null;
    e.target.src = getImageUrl(FALLBACK_IMAGE);
    e.target.classList.add("image-fallback");
  }, []);

  const showImage = can('home.col.image', 'Product image');
  const showName = can('home.col.name', 'Product name');
  const showCategory = can('home.col.category', 'Category');
  const showVatRate = can('home.col.vat_rate', 'VAT rate');
  const showPrice = can('home.col.price', 'Price');
  const showLineTotal = can('home.col.line_total', 'Line total');
  const showSku = can('home.col.sku', 'SKU');
  const showStockCol = can('home.col.stock', 'Stock');
  const showProductBadges = can('home.col.product_badges', 'Product badges');
  const showOrderHistory = can('home.section.order_history', 'Order history (usual / last order)');
  const showLicenceNotice = can('home.badge.licence_required', 'Premises licence notice');
  const quantityState = stateOf('home.field.quantity', 'Quantity');
  const addState = stateOf('home.btn.add_to_cart', 'Add to cart');
  const removeState = stateOf('home.btn.remove_from_cart', 'Remove from cart');

  return (
    <div className="w-full">
      {/* Header - Hide on mobile */}
      {!isMobile && (
        <div className="bg-gray-50 border-b border-gray-200 px-4 py-3 flex items-center text-sm font-medium text-gray-600">
          <div className="flex-shrink-0" style={{ width: '250px' }}>Product & Details</div>
          <div className="flex-1 text-right">Controls & Info</div>
        </div>
      )}

      {/* Products List */}
      <div className="divide-y divide-gray-200">
        {products.map((product, index) => {
          const currentQuantity = quantities[product.id] || '';
          const cartQuantity = getProductTotalQuantity(product.id);
          const price = parseFloat(product.price || 0);
          const totalAmount = cartQuantity * price;
          const actionState = actionStates[product.id] || { loading: false, success: false, error: false };
          const longPressState = longPressStates[product.id] || {};
          
          // Handle image same as other views
          const primaryImage = product.images?.find(img => img.is_primary)?.image || product.images?.[0]?.image || FALLBACK_IMAGE;
          
          
          // Get order statistics
          const orderStats = getOrderStatsForProduct(product.id);
          const hasOrderHistory = showOrderHistory && orderStats && orderStats.total_orders > 0;
          const hasActiveBadges = showProductBadges && product.badges && product.badges.filter(b => b.is_active).length > 0;
          
          return (
            <div
              key={product.id}
              ref={index === products.length - 1 ? lastProductRef : null}
              className="hover:bg-gray-50 transition-colors"
            >
              {/* Main Content - Image + Two Column Layout */}
              <div className={`flex ${isMobile ? 'px-2 py-3' : 'px-4 py-4'}`}>
                {/* Product Image - Full height covering both rows */}
                {showImage && (
                <div
                  className={`flex-shrink-0 ${isMobile ? 'w-16' : 'w-20'} mr-3`}
                  onMouseEnter={(e) => {
                    if (hoverZoomEnabled && !isMobile) {
                      const rect = e.currentTarget.getBoundingClientRect();
                      setHoverImage({
                        src: getImageUrl(primaryImage),
                        alt: product.name,
                        top: rect.top,
                        left: rect.right + 8,
                      });
                    }
                  }}
                  onMouseLeave={() => setHoverImage(null)}
                >
                  <img
                    src={getImageUrl(primaryImage)}
                    alt={product.name}
                    className="w-full h-full object-cover rounded-lg border border-gray-200"
                    loading="lazy"
                    style={{ imageRendering: 'auto' }}
                    onError={handleImageError}
                  />
                </div>
                )}

                {/* Two Rows Content */}
                <div className="flex-1 flex flex-col min-h-full">
                  {/* Product Name and Category (left aligned, stacked) */}
                  <div className="flex-1 flex flex-col justify-start">
                    {showName && (
                    <div className={`font-semibold text-gray-900 leading-tight ${isMobile ? 'text-sm' : 'text-base'}`}>
                      {product.name}
                    </div>
                    )}
                    {(() => {
                      let rate = null;
                      if (product.vat_rate_percentage != null && product.vat_rate_percentage !== '') {
                        rate = parseFloat(product.vat_rate_percentage);
                      } else if (product.vat_rate && product.vat_rate.rate != null) {
                        rate = parseFloat(product.vat_rate.rate);
                      } else if (product.vat_rate_name) {
                        const m = product.vat_rate_name.match(/([\d.]+)\s*%/);
                        if (m) rate = parseFloat(m[1]);
                      }
                      const valid = rate != null && !Number.isNaN(rate);
                      const formatted = valid
                        ? (Number.isInteger(rate) ? rate : rate.toFixed(2).replace(/\.?0+$/, ''))
                        : null;
                      const vatColor = (r) => {
                        if (r === 0) return 'bg-gray-100 text-gray-700 border-gray-300';
                        if (r <= 5) return 'bg-green-100 text-green-800 border-green-300';
                        if (r <= 10) return 'bg-lime-100 text-lime-800 border-lime-300';
                        if (r <= 15) return 'bg-yellow-100 text-yellow-800 border-yellow-300';
                        if (r <= 20) return 'bg-orange-100 text-orange-800 border-orange-300';
                        return 'bg-red-100 text-red-800 border-red-300';
                      };
                      const badge = showVatRate && valid && (
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded border text-[10px] font-mono font-semibold whitespace-nowrap ${vatColor(rate)}`}>
                          VAT {formatted}%
                        </span>
                      );
                      if (isMobile) {
                        return (
                          <div className="flex items-center justify-between gap-2">
                            <div className="text-gray-500 text-xs truncate">
                              {showCategory ? (product.category?.name || '') : ''}
                            </div>
                            {badge}
                          </div>
                        );
                      }
                      return (
                        <div className="flex items-center justify-between gap-2">
                          <div className="text-gray-500 text-sm">
                            {showCategory ? (product.category?.name || '') : ''}
                          </div>
                          {badge}
                        </div>
                      );
                    })()}
                  </div>

                  {/* Bottom Row - Price/Quantity Controls/Amount (right aligned) */}
                  <div className="flex items-center justify-end space-x-3">
                    {isAuthenticated ? (
                      <>
                        {/* Price/Unit */}
                        <div className={`text-gray-900 font-bold flex-shrink-0 ${isMobile ? 'text-sm' : 'text-base'}`}>
                          {showPrice && product.price ? (
                            <span>
                              £{price.toFixed(2)}/{product.base_unit || 'pcs'}
                            </span>
                          ) : (
                            <span>{product.base_unit || 'pcs'}</span>
                          )}
                        </div>

                        {product.alcohol_blocked ? (
                          showLicenceNotice ? (
                          <div className="text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 flex-shrink-0">
                            Premises licence required
                          </div>
                          ) : null
                        ) : (
                        <div className="flex items-center space-x-1 flex-shrink-0">
                          {/* Decrease Button */}
                          {quantityState !== 'hidden' && (<>
                          <button
                            onClick={() => handleDecreaseCartQuantity(product)}
                            onMouseDown={removeState === 'visible' ? () => handleLongPressStart(product.id) : undefined}
                            onMouseUp={removeState === 'visible' ? () => handleLongPressEnd(product.id) : undefined}
                            onMouseLeave={removeState === 'visible' ? () => handleLongPressEnd(product.id) : undefined}
                            onTouchStart={removeState === 'visible' ? () => handleLongPressStart(product.id) : undefined}
                            onTouchEnd={removeState === 'visible' ? () => handleLongPressEnd(product.id) : undefined}
                            onTouchCancel={removeState === 'visible' ? () => handleLongPressEnd(product.id) : undefined}
                            className={`rounded-md border flex items-center justify-center font-bold disabled:opacity-50 ${isMobile ? 'w-8 h-8' : 'w-9 h-9 text-lg'} ${
                              longPressState.clearing ? 'border-orange-300 bg-orange-100 text-orange-600' :
                              longPressState.cleared ? 'border-green-300 bg-green-100 text-green-600' :
                              longPressState.longPressing ? 'border-red-500 bg-red-200 text-red-700 animate-pulse' :
                              'border-red-300 bg-red-50 text-red-600 hover:bg-red-100'
                            }`}
                            type="button"
                            disabled={actionState.loading || cartQuantity === 0 || longPressState.clearing || quantityState === 'readonly'}
                            title={longPressState.longPressing ? 'Hold to clear all...' : `Remove ${product.step_quantity || 1} from cart (hold 2s to clear all)`}
                          >
                            {longPressState.clearing ? '⏳' : longPressState.cleared ? '✓' : '-'}
                          </button>

                          {/* Quantity Input */}
                          <input
                            type="text"
                            value={currentQuantity !== undefined && currentQuantity !== '' ? currentQuantity : (cartQuantity > 0 ? cartQuantity.toString() : '')}
                            onChange={(e) => handleQuantityChange(product.id, e.target.value)}
                            onFocus={() => handleQuantityFocus(product.id)}
                            onKeyPress={quantityState === 'editable' ? (e) => handleQuantityKeyPress(e, product) : undefined}
                            onBlur={quantityState === 'editable' ? (e) => handleQuantityBlur(e, product) : undefined}
                            className={`px-2 text-center border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 no-spinner ${
                              isMobile ? 'w-16 h-8 text-sm' : 'w-20 h-9 text-sm'
                            } ${
                              actionState.loading ? 'border-yellow-300 bg-yellow-50' : 
                              actionState.success ? 'border-green-300 bg-green-50' :
                              actionState.error ? 'border-red-300 bg-red-50' :
                              cartQuantity > 0 ? 'border-green-300 bg-green-50' :
                              'border-gray-300'
                            }`}
                            placeholder="0"
                            disabled={actionState.loading}
                            readOnly={quantityState === 'readonly'}
                          />
                          </>)}

                          {/* Increase Button - Add to Cart */}
                          {addState !== 'hidden' && (
                          <button
                            onClick={() => handleDirectAddToCart(product)}
                            className={`rounded-md border border-green-300 bg-green-50 flex items-center justify-center text-green-600 hover:bg-green-100 disabled:opacity-50 font-bold ${isMobile ? 'w-8 h-8' : 'w-9 h-9 text-lg'}`}
                            type="button"
                            disabled={actionState.loading || addState === 'disabled'}
                            title={`Add ${cartQuantity === 0 ? (product.min_quantity || 1) : (product.step_quantity || 1)} to cart`}
                          >
                            +
                          </button>
                          )}
                        </div>
                        )}

                        {/* Total Amount */}
                        {showLineTotal && (
                        <div className={`font-semibold flex-shrink-0 ${isMobile ? 'text-sm min-w-[60px]' : 'text-sm min-w-[80px]'} text-right`}>
                          {cartQuantity > 0 && price > 0 ? (
                            <span className="text-green-600">£{totalAmount.toFixed(2)}</span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </div>
                        )}
                      </>
                    ) : null}
                  </div>
                </div>
              </div>



              {/* Bottom Border - SKU, Badges, Order History as compact cards on left */}
              {(hasOrderHistory || (showSku && product.sku) || (showStock && showStockCol) || (showProductBadges && product.badges && product.badges.length > 0)) && (
                <div className="flex items-center gap-0 w-full border-t border-gray-200">
                  {showSku && product.sku && (
                    <div className="px-3 py-1.5 border-r border-gray-300 bg-white">
                      <div className="text-xs font-mono text-gray-700 whitespace-nowrap">
                        SKU: {product.sku}
                      </div>
                    </div>
                  )}
                  {showStock && showStockCol && (
                    <div className="px-3 py-1.5 border-r border-gray-300 bg-white">
                      <div className="text-xs text-gray-700 whitespace-nowrap">
                        Stock: <span className="font-medium">{parseFloat(product.stock || 0)} {product.base_unit || product.unit?.symbol || ''}</span>
                        {parseFloat(product.reserved || 0) > 0 && (
                          <span className="text-amber-600"> (-{parseFloat(product.reserved)})</span>
                        )}
                      </div>
                    </div>
                  )}
                  {hasActiveBadges && (
                    <div className="flex items-center gap-1 px-2 py-1.5 border-r border-gray-300 bg-white">
                      {product.badges.filter(b => b.is_active).map((badge) => (
                        <span
                          key={badge.id}
                          className="text-[9px] px-1.5 py-0.5 rounded shadow-sm font-medium whitespace-nowrap"
                          style={{ backgroundColor: badge.color, color: badge.text_color }}
                          title={badge.name}
                        >
                          {badge.name}
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="flex-1" />
                  {hasOrderHistory && (
                    <div className="px-3 py-1.5 border-l border-gray-300 bg-blue-50">
                      <div className="text-xs font-semibold text-blue-700 whitespace-nowrap">
                        {`USUAL: ${orderStats.usual_quantity || 0} ${orderStats.usual_unit || ''}`.trim()}
                      </div>
                    </div>
                  )}
                  {hasOrderHistory && (
                    <div className="px-3 py-1.5 border-l border-gray-300 bg-green-50">
                      <div className="text-xs font-semibold text-green-700 whitespace-nowrap">
                        {`LAST: ${orderStats.last_quantity || 0} ${orderStats.last_unit || ''} ${orderStats.last_order_date ? new Date(orderStats.last_order_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : ''}`.trim()}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {hoverImage && (
        <div
          className="fixed z-[9999] p-1 bg-white rounded-lg shadow-xl border border-gray-200 pointer-events-none"
          style={{ top: hoverImage.top, left: hoverImage.left }}
        >
          <img
            src={hoverImage.src}
            alt={hoverImage.alt}
            className="w-48 h-48 object-cover rounded-lg"
            onError={handleImageError}
          />
        </div>
      )}
    </div>
  );
};

export default WsListView;